import { z } from 'zod';

export const uuidSchema = z.uuid({ message: 'Must be a valid id' });

export const idParamSchema = z.object({ id: uuidSchema });
export type IdParam = z.infer<typeof idParamSchema>;

export const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

/**
 * Every list endpoint takes this shape. Page size is capped server-side so a
 * client cannot ask for an unbounded scan.
 */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  sortBy: z.string().min(1).max(64).optional(),
  sortDir: z.enum(SORT_DIRECTIONS).default('desc'),
  search: z.string().trim().min(1).max(200).optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/**
 * The advanced filter builder's condition tree, as a list endpoint receives
 * it: JSON-encoded in one query parameter, there being nowhere else to put
 * structured data on a GET. Only mixed into the list endpoints that actually
 * apply it — accepting it and silently ignoring it would be worse than not
 * accepting it at all.
 */
export const listFiltersQueryFragment = {
  filters: z.string().max(20_000).optional(),
};

export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface Paginated<T> {
  data: T[];
  meta: PageMeta;
}

export function buildPageMeta(page: number, pageSize: number, total: number): PageMeta {
  const totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
  return {
    page,
    pageSize,
    total,
    totalPages,
    hasNext: page < totalPages,
    hasPrevious: page > 1 && total > 0,
  };
}

/** Machine-readable codes carried by every error response. */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'PAYLOAD_TOO_LARGE',
  'CSRF_FAILED',
  'INTERNAL_ERROR',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface FieldError {
  path: string;
  message: string;
}

/** The one and only error envelope the API returns. */
export interface ApiErrorBody {
  statusCode: number;
  code: ErrorCode;
  message: string;
  details?: FieldError[];
  path: string;
  timestamp: string;
  requestId?: string;
}

export const emailSchema = z
  .email({ message: 'Enter a valid email address' })
  .trim()
  .toLowerCase()
  .max(254);

/** Permissive by design: international numbers vary far too much to validate. */
export const phoneSchema = z
  .string()
  .trim()
  .max(32)
  .regex(/^[+()\-.\s\d]*$/u, 'Enter a valid phone number');

/**
 * An optional free-text field.
 *
 * An empty string becomes `null`, not `undefined`, and the distinction
 * matters: on a PATCH, `undefined` means "I did not mention this field" while
 * `null` means "clear it". Collapsing the two would make it impossible to
 * empty a field once it had a value — the form would submit, report success,
 * and change nothing.
 */
export const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value.length === 0 ? null : value))
    .nullish();

/** Same clearing semantics for URL fields. */
export const optionalUrl = () =>
  z
    .union([z.url(), z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullish();

/**
 * An optional email address.
 *
 * A text input that was never typed into submits an empty string, not
 * `undefined` — so `emailSchema.optional()` rejects every form where the user
 * simply left the field alone. Accepting '' and mapping it to null is what
 * makes "optional" actually optional, and doubles as the way to clear it.
 */
export const optionalEmail = () =>
  z
    .union([emailSchema, z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullish();

/** Same treatment for phone numbers, which are free-form but clearable. */
export const optionalPhone = () =>
  z
    .union([phoneSchema, z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullish();

/**
 * An optional calendar date (`<input type="date">`).
 *
 * An empty date input submits '', which `z.iso.date()` rejects — so without
 * this, any form containing an optional date could not be submitted with that
 * date left blank.
 */
export const optionalDate = () =>
  z
    .union([z.iso.date(), z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullish();

/** The same, for `<input type="datetime-local">` values. */
export const optionalDateTime = () =>
  z
    .union([z.iso.datetime({ offset: true }), z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullish();

/**
 * An optional number backed by a number input.
 *
 * `<input type="number">` submits `''` when it is empty, and
 * `z.coerce.number()` turns `''` into 0 — so a blank optional field either
 * stores a spurious zero or, where the schema has a `min` above zero, blocks
 * the form entirely. Catching `''` before coercion is the only way to tell
 * "left blank" apart from "typed a zero".
 *
 * Takes the constrained number schema so callers keep their own bounds:
 * `optionalNumber(z.coerce.number().int().min(1).max(20_000))`.
 *
 * Blank becomes `null`, which clears a nullable column. For a column that
 * cannot be null — a deal's probability always has a value — use
 * `blankableNumber`, where blank means "leave it alone" instead.
 */
export const optionalNumber = <T extends z.ZodType>(schema: T) =>
  z
    .union([z.literal(''), z.null(), schema])
    .transform((value) => (value === '' ? null : value))
    .optional();

/**
 * The same, for a column that cannot be null: a blank input means "unchanged"
 * rather than "clear it", so it drops out of the payload entirely.
 */
export const blankableNumber = <T extends z.ZodType>(schema: T) =>
  z
    .union([z.literal(''), schema])
    .transform((value) => (value === '' ? undefined : value))
    .optional();

/**
 * An optional enum backed by a `<select>`.
 *
 * The "no choice" option in a select submits `''`, which `z.enum()` rejects —
 * so without this, a form whose optional dropdown is left on its default
 * cannot be submitted at all, and React Hook Form blocks it with an error
 * nobody is showing. Found exactly that way: the email template form was
 * unsubmittable with "Every module" selected.
 */
export const optionalEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .union([z.enum(values), z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullish();

/**
 * Builds a PATCH schema from a POST schema.
 *
 * `createSchema.partial()` is the obvious way to do this and it is wrong:
 * Zod's `.partial()` makes a field optional but leaves its `.default()` in
 * place, so a patch that omits the field parses as the default rather than as
 * "unchanged". A PATCH of `{ description }` against a deal was resetting
 * `amount` to 0, and against a lead was resetting `status` to NEW — silent
 * data loss on the most ordinary edit there is.
 *
 * So: strip the default, then make the field optional. Every other constraint
 * (`min`, `max`, `email`, the enum's member list) is preserved, because only
 * the outermost ZodDefault wrapper is removed.
 *
 * Nested defaults are deliberately left alone. A default inside an array
 * element only applies when the caller sends that element, which is exactly
 * the create-time semantics you want.
 */
export function toPatchSchema<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
): z.ZodObject<{ [K in keyof T]: z.ZodOptional<T[K]> }> {
  const patched: Record<string, z.ZodTypeAny> = {};

  // `schema.shape` is typed as a readonly index signature of the core
  // `$ZodType`, which has none of the class methods; the cast recovers the
  // concrete type the shape actually holds.
  const shape = schema.shape as unknown as Record<string, z.ZodTypeAny>;
  for (const [key, field] of Object.entries(shape)) {
    patched[key] = stripDefault(field).optional();
  }

  return z.object(patched) as z.ZodObject<{ [K in keyof T]: z.ZodOptional<T[K]> }>;
}

/** Unwraps `.default()` / `.prefault()`, leaving every other wrapper intact. */
function stripDefault(field: z.ZodTypeAny): z.ZodTypeAny {
  const kind = (field as { def?: { type?: string } }).def?.type;
  if (kind !== 'default' && kind !== 'prefault') return field;

  const unwrap = (field as unknown as { unwrap?: () => z.ZodTypeAny }).unwrap;
  return typeof unwrap === 'function' ? stripDefault(unwrap.call(field)) : field;
}
